import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
} from "@mui/material";
import statisticAPI from "../../Services/Admin/statisticAPI";
import Chart from "react-apexcharts";
import { formatDistanceToNow, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import AuthContext from "../../Contexts/AuthContext";
import Grid from "@mui/material/Grid2";

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

const Main = () => {
  const { firstname = "Utilisateur" } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [quickHistory, setQuickHistory] = useState({
    daily_stats: [],
    latest_users_connected: [],
    latest_users_registered: [],
    all_users_count: 0,
    active_users_since_october: 0,
  });

  const getQuickStat = useCallback(async () => {
    setLoading(true);
    try {
      const data = await statisticAPI.getQuickHistory();
      console.log(data);
      setQuickHistory(data);
    } catch (error) {
      console.log(error.response);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    getQuickStat();
  }, [getQuickStat]);

  const data = quickHistory.daily_stats || [];
  const formattedDates = data.map((stat) => {
    const date = new Date(stat.date);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  });

  const series = [
    {
      name: "Connexions",
      type: "column",
      data: data.map((stat) => stat.connection_count),
    },
    {
      name: "Utilisateurs Uniques",
      type: "line",
      data: data.map((stat) => stat.unique_user_count),
    },
  ];

  const options = {
    chart: {
      height: 300,
      type: "line",
      stacked: false,
    },
    stroke: {
      width: [0, 3],
      curve: "smooth",
    },
    markers: {
      size: [0, 5],
      strokeColors: "#6A0DAD",
      hover: {
        size: 7,
      },
    },
    plotOptions: {
      bar: {
        columnWidth: "40%",
      },
    },
    colors: ["#FFA500", "#6A0DAD"],
    dataLabels: {
      enabled: false,
    },
    labels: formattedDates,
    xaxis: {
      type: "category",
      labels: {
        style: {
          colors: "#888",
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#888",
        },
      },
      min: 0,
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
    },
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 12 }}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6">Bienvenue, {firstname} 👋</Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Votre progression est impressionnante !
            </Typography>
            <Box
              display="flex"
              justifyContent="space-around"
              alignItems="center"
            >
              <Box textAlign="center">
                <Typography variant="h5">
                  {quickHistory.all_users_count}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Utilisateurs inscrits
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h5">
                  {quickHistory.active_users_since_october}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Utilisateurs actifs
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography variant="h6">Historique</Typography>
                <Typography variant="body2" color="textSecondary">
                  Statistiques des 7 derniers jours
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                sx={{
                  background: "linear-gradient(to right, #ff6b6b, #f06595)",
                }}
              >
                Détails
              </Button>
            </Box>

            {data.length > 0 ? (
              <Chart
                options={options}
                series={series}
                type="line"
                height={300}
              />
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <Typography variant="body2" color="textSecondary">
                  Aucune donnée disponible pour les 7 derniers jours
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6">Dernières connexions</Typography>
            </Box>
            <Divider />
            <List>
              {quickHistory.latest_users_connected.slice(0, 4).map((user) => (
                <ListItem key={user.id} sx={{ paddingY: 1 }}>
                  <ListItemAvatar>
                    <Avatar src={`/path/to/avatars/${user.id}.png`} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${capitalize(user.firstname || "")} ${capitalize(
                      user.lastname || ""
                    )}`}
                    secondary={`${capitalize(user.speciality || "")}`}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {user.date
                      ? formatDistanceToNow(parseISO(user.date), {
                          addSuffix: true,
                          locale: fr,
                        })
                      : "Date inconnue"}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6">
              Derniers utilisateurs enregistrés
            </Typography>
            <Divider sx={{ my: 1 }} />
            <List>
              {quickHistory.latest_users_registered.slice(0, 5).map((user) => (
                <ListItem
                  key={user.id}
                  alignItems="flex-start"
                  sx={{ paddingY: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar src={`/path/to/avatars/${user.id}.png`} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${capitalize(user.firstname || "")} ${capitalize(
                      user.lastname || ""
                    )}`}
                    secondary={
                      user.validatedAt
                        ? `Compte activé le ${new Date(
                            user.validatedAt.date
                          ).toLocaleDateString("fr-FR")}`
                        : "Compte non activé"
                    }
                  />
                  <Typography variant="body2" color="textSecondary">
                    {user.validatedAt && user.validatedAt.date
                      ? formatDistanceToNow(parseISO(user.validatedAt.date), {
                          addSuffix: true,
                          locale: fr,
                        })
                      : "En attente de validation"}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Main;
